// src/pages/TicketRecall/modals/RecallActionConfirmModal.tsx
// Modal xác nhận Confirm/Cancel phiếu thu hồi vé ế — bản rút gọn của DeliveryActionConfirmModal.tsx.
// Dòng "Nguồn" hiển thị Kho tổng hoặc tên đại lý, vì phiếu có thể trừ tồn kho tổng hoặc tồn kho
// riêng của 1 đại lý tuỳ theo agentId của phiếu.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { AgentMap, getAgentName } from 'helpers/agentLookup';
import { TicketRecall } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_recall');

export type RecallActionType = 'confirm' | 'cancel';

interface RecallActionConfirmModalProps {
  isOpen: boolean;
  action: RecallActionType;
  recall: TicketRecall | null;
  agentMap: AgentMap;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
}

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '-';

const RecallActionConfirmModal: React.FC<RecallActionConfirmModalProps> = ({
  isOpen,
  action,
  recall,
  agentMap,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  if (!recall) return null;

  const isConfirm = action === 'confirm';

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        <i
          className={`${isConfirm ? 'ri-check-double-line text-success' : 'ri-close-circle-line text-danger'} me-2`}
        ></i>
        {isConfirm ? st('xac_nhan_thu_hoi_ve_e_text') : st('huy_phieu_thu_hoi_ve_e_text')}
      </ModalHeader>
      <ModalBody>
        <Alert color={isConfirm ? 'info' : 'warning'} className="py-2">
          <i className="ri-information-line me-1"></i>
          {isConfirm
            ? st('sau_khi_xac_nhan_he_thong_text', { value: recall.agentId ? st('cua_dai_ly_value_text', { value: getAgentName(agentMap, recall.agentId) }) : st('tong_text_2') })
            : st('phieu_sau_khi_huy_se_khong_text')}
        </Alert>

        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <tbody>
              <tr>
                <td width="30%" className="fw-semibold bg-light">{st('ma_phieu_column')}</td>
                <td>{recall.recallCode}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ke_hoach_phat_hanh_column')}</td>
                <td>{recall.planCode || recall.issuancePlanId}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('nguon_thu_hoi_label')}</td>
                <td>{recall.agentId ? getAgentName(agentMap, recall.agentId) : st('kho_tong_text')}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('tong_so_ve_text')}</td>
                <td className="fw-semibold">{formatNumber(recall.totalQuantity)}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ngay_lap_column')}</td>
                <td>{recall.createdAt ? moment(recall.createdAt).format('DD/MM/YYYY HH:mm') : '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {(recall.lines || []).length > 0 && (
          <div className="table-responsive mt-3">
            <table className="table table-sm table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>{st('ky_hieu_column')}</th>
                  <th>{st('tu_so_column')}</th>
                  <th>{st('den_so_column')}</th>
                  <th className="text-end">{st('so_luong_column')}</th>
                </tr>
              </thead>
              <tbody>
                {(recall.lines || []).map((line, index) => (
                  <tr key={line.id ?? index}>
                    <td>{line.symbol}</td>
                    <td>{line.rangeStart}</td>
                    <td>{line.rangeEnd}</td>
                    <td className="text-end">{formatNumber(line.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isConfirm && (
          <FormGroup className="mt-3 mb-0">
            <Label for="recall-cancel-reason">{st('ly_do_huy_label')}</Label>
            <Input
              type="textarea"
              id="recall-cancel-reason"
              rows={3}
              maxLength={500}
              placeholder={st('nhap_ly_do_huy_phieu_khong_placeholder')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={loading}>
          {st('dong_button')}
        </Button>
        <Button
          color={isConfirm ? 'success' : 'danger'}
          disabled={loading}
          onClick={() => onConfirm(isConfirm ? undefined : reason.trim() || undefined)}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              {st('dang_xu_ly_text')}
            </>
          ) : (
            <>
              <i className={`${isConfirm ? 'ri-check-line' : 'ri-close-line'} me-1`}></i>
              {isConfirm ? st('xac_nhan_thu_hoi_title') : st('huy_phieu_title')}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RecallActionConfirmModal;
